import { Observable } from 'rxjs/Observable';
import { RootModel, RootModelLists, RootModelObjects } from './root.model';

export interface RawEntity<Entity> {
  entity: Entity;
  timeFetched: number;
  timeUpdated: number;
}

export interface RawEntities<Entity> {
  [id: string]: RawEntity<Entity>;
}

export interface Lists<Entity> {
  [query: string]: List<Entity> | undefined;
}

export interface List<Entity> {
  ids: string[];
  query: Query<Entity>;
}

export interface Reference<T extends keyof RootModel> {
  path: Path<T>;
  query?: Query<RootModelEntity<T>>;
}

export interface ReferenceList<T extends keyof RootModel> {
  pathList: PathList<T>;
  query?: Query<RootModelEntity<T>>;
}

export interface Pagination {
  limit?: number;
  offset?: number;
}

export type Path<T extends keyof RootModel> = [T, string];
export type PathList<T extends keyof RootModel> = [T, string[]];

export function pathToPathList<T extends keyof RootModel>(
  path: Path<T>,
): PathList<T> {
  return [path[0], [path[1]]];
}

export function firstPathFromPathList<T extends keyof RootModel>(
  pathList: PathList<T>,
): Path<T> {
  return [pathList[0], pathList[1][0]];
}

export function referenceToReferenceList<T extends keyof RootModel>(
  ref: Reference<T>,
): ReferenceList<T> {
  return { pathList: pathToPathList(ref.path), query: ref.query };
}

export function firstReferenceFromReferenceList<T extends keyof RootModel>(
  refList: ReferenceList<T>,
): Reference<T> {
  return {
    path: firstPathFromPathList(refList.pathList),
    query: refList.query,
  };
}

// Queries that you can pass to an endpoint.
export type Query<Entity> = Pagination & {
  params?: QueryParams<Entity>;
};

export type QueryParam<Entity, K extends keyof Entity = keyof Entity> =
  | {
      name: K;
      condition: '='; // TODO: Include ranges like < and >
      value: Entity[K];
    }
  | {
      name: K;
      condition: 'in';
      values: Entity[K][];
    };

export type QueryParams<Entity> = {
  [K in keyof Partial<Entity>]: QueryParam<Entity, K>
};

export interface RawEntityList<T> {
  entities: RawEntities<T>;
  lists: Lists<T>;
}

export type RawStoreObjectStateSlice<
  T extends keyof RootModelObjects
> = RootModelObjectToRawStoreStateSlice<RootModelObjects[T]>;

export type RawStoreListStateSlice<
  T extends keyof RootModelLists
> = RootModelListToRawStoreStateSlice<RootModelLists[T]>;

type RawStoreObjectState = RootModelObjectToRawStoreObjectState<
  RootModelObjects
>;
export type RawStoreListState = RootModelListToRawStoreListState<
  RootModelLists
>;
export type RawStoreState = RawStoreListState & RawStoreObjectState;

export type RootModelEntity<T extends keyof RootModel> = (RootModelLists &
  RootModelObjects)[T][string];

type RootModelObjectToRawStoreStateSlice<T> = {
  [P in keyof T]: RawEntity<T[P]>
};

type RootModelListToRawStoreStateSlice<T> = {
  [P in keyof T]: RawEntityList<T[P]>
};

type RootModelObjectToRawStoreObjectState<T> = {
  [P in keyof T]: { [Q in keyof T[P]]: RawEntity<T[P][Q]> }
};

type RootModelListToRawStoreListState<T> = {
  [P in keyof T]: { [Q in keyof T[P]]: RawEntityList<T[P][Q]> }
};

type RootModelToRawStoreEntities<
  T,
  P extends keyof T,
  Q extends keyof T[P]
> = RawEntities<T[P][Q]>;

function compare<T>(a: T, b: T) {
  return a < b ? -1 : a > b ? 1 : 0;
}

// If a rawEntity matches a query. If there's no query then it does so trivially.
function rawEntityMatchesQueryParams<Entity>(
  rawEntity: RawEntity<Entity>,
  params?: QueryParams<Entity>,
) {
  return params
    ? Object.values(params).every((param: QueryParam<Entity>) => {
        if (param.name in rawEntity.entity) {
          switch (param.condition) {
            case '=':
              return rawEntity.entity[param.name] === param.value;
            case 'in':
              return param.values.includes(rawEntity.entity[param.name]);
            default:
              return assertNever(param);
          }
        }

        return false;
      })
    : true;
}

export function stringifyQuery<T extends keyof RootModel>(
  query: Query<RootModelEntity<T>> | undefined,
) {
  if (query) {
    // Format to [key, condition, value], ignoring undefined values
    // Get all the params, filter out the params with undefined or null as
    // values just to be defensive.
    const params = query.params
      ? // TODO: QueryParams entries are too complicated for TypeScript to
        // understand that it suddenly just assumes {}
        Object.entries(<QueryParams<{ [k: string]: any }>>query.params)
          .filter(([key, entry]) => !!entry)
          .map(([key, entry]) => [
            key,
            entry.condition,
            (entry.condition === '=' &&
              entry.value &&
              entry.value.toString()) ||
              (entry.condition === 'in' && [...entry.values].sort().toString()),
          ])
      : [];

    const pagination = { ...query, params: undefined };

    const entries = Object.entries(pagination)
      .filter(([key, value]) => !!value)
      .map(([key, value]) => [key, '=', value])
      .concat(params)
      .sort()
      .map(([key, condition, value]) => `${key}${condition}${value}`)
      .join(';');

    return entries || 'default';
  }

  return 'default';
}

/* This merges a number of entities into the state.
 * If an offset it specified then the new entities are placed at that offset.
 * If an offset is not specified then the new entities will be:
 *   - merged if they have the same id as an existing entity OR
 *   - placed at the start of the list (and the offset of existing items moved up)
 *
 * We also allow a custom sorting function to allow resorting the list.
 * If this is used all the offsets will be then be reindexed from 0.
 */
export function mergeEntities<T extends keyof RootModelLists>(
  state: RawStoreListState[T],
  entities: RootModelToRawStoreEntities<RootModelLists, T, string>,
  ref: Reference<T>,
): RawStoreListState[T] {
  const data =
    state[ref.path[1]] && state[ref.path[1]].entities
      ? state[ref.path[1]]
      : { entities: {}, lists: {} };
  const queryString = stringifyQuery(ref.query);

  const updatedEntities = {
    ...data.entities,
    ...entities,
  };

  const newListIds = Object.keys(entities);

  return Object.assign({}, state, {
    [ref.path[1]]: {
      entities: updatedEntities,
      lists: {
        ...data.lists,
        ...{
          [queryString]: {
            ids: newListIds,
            query: ref.query,
            // TODO: sortField information in string, not function
          },
        },
      },
    },
  });
}

/* This merges a number of entities into the state.
 * If an offset it specified then the new entities are placed at that offset.
 * If an offset is not specified then the new entities will be:
 *   - merged if they have the same id as an existing entity OR
 *   - placed at the start of the list (and the offset of existing items moved up)
 *
 * We also allow a custom sorting function to allow resorting the list.
 * If this is used all the offsets will be then be reindexed from 0.
 * TODO T40096: This is a websocket version of mergeEntities, which has more
 * logic right now to apply the websocket data on the right place of the store
 * slice. Maybe we can merge this with mergeEntities again?
 */
export function mergeWebsocketEntities<T extends keyof RootModelLists>(
  state: RawStoreListState[T],
  entities: RootModelToRawStoreEntities<RootModelLists, T, string>,
  ref: Reference<T>,
  sortField?: (entity: RootModelLists[T][string]) => number | string,
): RawStoreListState[T] {
  const data: RawEntityList<RootModelEntity<T>> =
    state[ref.path[1]] && state[ref.path[1]].entities
      ? state[ref.path[1]]
      : { entities: {}, lists: {} };

  const updatedEntities = {
    ...data.entities,
    ...entities,
  };

  if (!data.lists) {
    return state;
  }

  const updatedLists = Object.entries(data.lists).reduce(
    (obj, [key, entry]) => {
      // TODO: Type annotation is unnecessary if List.query is typed properly.
      const { query }: { query?: Query<RootModelEntity<T>> } = entry || {
        query: {},
      };

      const { params, offset } = query || {
        params: undefined,
        offset: undefined,
      };

      // If the offset is non-zero we don't add it to the list.
      if (offset) {
        return obj;
      }

      let filteredEntityEntries = Object.entries(updatedEntities);

      filteredEntityEntries = filteredEntityEntries.filter(
        ([entityKey, rawEntity]) =>
          rawEntityMatchesQueryParams(rawEntity, params),
      );

      if (sortField) {
        filteredEntityEntries.sort(([keyA, rawEntityA], [keyB, rawEntityB]) =>
          compare(sortField(rawEntityA.entity), sortField(rawEntityB.entity)),
        );
      }

      const updatedEntityIds = filteredEntityEntries
        .slice(offset) // TODO: `limit` doesn't really work well without sortField
        .map(([entityId]) => entityId);

      return {
        ...obj,
        [key]: {
          ...entry,
          ids: updatedEntityIds,
        },
      };
    },
    {} as Lists<RootModelEntity<T>>,
  );

  return Object.assign({}, state, {
    [ref.path[1]]: {
      entities: updatedEntities,
      lists: {
        ...data.lists,
        ...updatedLists,
      },
    },
  });
}

export function mergeListEntities<T extends keyof RootModelLists>(
  state: RawStoreListState[T],
  entities: RootModelToRawStoreEntities<RootModelLists, T, string>,
  refList: ReferenceList<T>,
  groupKey: keyof RootModelLists[T][string],
  sortField?: (entity: RootModelLists[T][string]) => number | string,
): RawStoreListState[T] {
  return refList.pathList[1].reduce((newState, section) => {
    const ref: Reference<T> = {
      path: [refList.pathList[0], section],
      query: refList.query,
    };
    const theseEntities = objectFilter(
      entities,
      (id, rawEntity) => String(rawEntity.entity[groupKey]) === section,
    );

    return mergeEntities(newState, theseEntities, ref);
  }, state);
}

function entityToRawEntity<E>(entity: E): RawEntity<E> {
  return {
    entity,
    timeFetched: Date.now(),
    timeUpdated: Date.now(),
  };
}

export function mergeObject<T extends keyof RootModelObjects>(
  state: RawStoreObjectState[T],
  object: RootModelObjects[T]['string'],
  ref: Reference<T>,
): RawStoreObjectState[T] {
  return Object.assign({}, state, { [ref.path[1]]: entityToRawEntity(object) });
}

export function mergeMultipleObjects<T extends keyof RootModelObjects>(
  state: RawStoreObjectState[T],
  objects: (RootModelObjects[T]['string'] & { id: number | string })[],
): RawStoreObjectState[T] {
  const newState: RawStoreObjectState[T] = objects.reduce(
    (acc: RawStoreObjectState[T], e) =>
      Object.assign({}, acc, { [e.id]: entityToRawEntity(e) }),
    {},
  );

  return Object.assign({}, state, newState);
}

export function transformObjects<
  T extends { id: number | string; type?: string },
  O
>(
  list: O[] | { [id: number]: O } | undefined,
  transform: (a: O) => T | undefined,
): RawEntities<T>;
export function transformObjects<
  T extends { id: number | string; type?: string },
  O,
  E
>(
  list: O[] | { [id: number]: O } | undefined,
  transform: (a: O, extra: E) => T | undefined,
  extra: E,
): RawEntities<T>;
export function transformObjects<
  T extends { id: number | string; type?: string },
  O,
  E
>(
  list: O[] | { [id: number]: O } | undefined,
  transform: (a: O, extra?: E) => T | undefined,
  extra?: E,
): RawEntities<T> {
  if (!list) {
    return {};
  }

  const actualList = Array.isArray(list) ? list : Object.values<O>(list);

  return actualList
    .map(o => transform(o, extra))
    .filter(isDefined)
    .map(entity => ({
      [entity.type ? `${entity.type}-${entity.id}` : entity.id]: {
        entity,
        timeFetched: Date.now(),
        timeUpdated: Date.now(),
      },
    }))
    .reduce((acc: RawEntities<T>, item) => ({ ...acc, ...item }), {});
}

export function pathListEqual<T extends keyof RootModel>(
  a: PathList<T>,
  b: PathList<T>,
) {
  return a[0] === b[0] && sameElements(a[1], b[1]);
}

export function referenceListsEqual<T extends keyof RootModel>(
  a: ReferenceList<T>,
  b: ReferenceList<T>,
) {
  return (
    pathListEqual(a.pathList, b.pathList) &&
    ((a.query === undefined && b.query === undefined) ||
      (a.query !== undefined &&
        b.query !== undefined &&
        a.query.offset === b.query.offset &&
        a.query.limit === b.query.limit &&
        a.query.params === b.query.params))
  );
}

export function sameElements<T>(as: T[], bs: T[]) {
  const setAs = new Set(as);
  const setBs = new Set(bs);
  return as.every(a => setBs.has(a)) && bs.every(b => setAs.has(b));
}

// All the (distinct) elements in `as` that isn't in `bs`.
export function setDiff<T>(as: T[], bs: T[]) {
  const setAs = new Set(as);
  const setBs = new Set(bs);
  return Array.from(setAs).filter(a => !setBs.has(a));
}

export function assertNever(
  x: never,
  error: string = 'Unexpected object',
): never {
  throw new Error(`${error}: ${JSON.stringify(x)}`);
}

export function uniq<T>(
  xs: T[],
  compareFunction: (a: T, b: T) => boolean = (a: T, b: T) => a === b,
): T[] {
  const compareWith = (x: T) => (_: T) => compareFunction(x, _);
  return xs.reduce(
    (acc: T[], x: T) => (acc.find(compareWith(x)) ? acc : [...acc, x]),
    [],
  );
}

export function isDefined<T>(x?: T): x is T {
  return x !== undefined;
}

export function objectFilter<V>(
  object: { [key: string]: V },
  predicate: (key: string, v: V) => boolean,
): { [key: string]: V } {
  return Object.entries(object)
    .filter(([key, value]) => predicate(key, value))
    .reduce((obj: { [key: string]: V }, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
}

export function objectFilterByKeys<V>(
  object: { [key: string]: V },
  keys: string[],
) {
  return objectFilter(object, key => keys.includes(key));
}

export function pluckListItemFromRawStore<T extends keyof RootModelLists>(
  store: RawStoreListState,
  path: Path<T>,
  id: string | number,
): RootModelEntity<T> | undefined {
  return store[path[0]][path[1]].entities[id]
    ? store[path[0]][path[1]].entities[id].entity
    : undefined;
}

export function pluckListItemFromRawStoreSlice<T extends keyof RootModelLists>(
  storeSlice: RawStoreListStateSlice<T>,
  path: Path<T>,
  id: string | number,
): RootModelEntity<T> | undefined {
  if (!storeSlice[path[1]] || !storeSlice[path[1]].entities[id]) {
    return undefined;
  }
  return storeSlice[path[1]].entities[id].entity;
}

export function removeEntityById<T extends keyof RootModelLists>(
  ref: Reference<T>,
  state: RawStoreListState[T],
  id: string | number,
): RawStoreListState[T] {
  const data = state[ref.path[1]] || { lists: {}, entities: {} };
  const entities = { ...data.entities };
  delete entities[id];

  // Remove the deleted ID from any list
  const lists = Object.entries(<Lists<RootModelEntity<T>>>data.lists).reduce(
    (obj, [queryString, list]) => {
      const newIds = (list && list.ids ? list.ids : []).filter(
        listId => listId.toString() !== id.toString(),
      );

      return {
        ...obj,
        [queryString]: {
          ...list,
          ids: newIds,
        },
      };
    },
    <Lists<RootModelEntity<T>>>{},
  );

  return Object.assign({}, state, {
    [ref.path[1]]: {
      entities,
      lists,
    },
  });
}

export function arrayIsShallowEqual<T>(a?: T[], b?: T[]) {
  return Boolean(
    a &&
      b &&
      a.length === b.length &&
      a.every((entity, index) => entity === b[index]),
  );
}

export function toObservable<T>(x: T | Observable<T>): Observable<T> {
  return x instanceof Observable ? x : Observable.of(x);
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-webapp-compat/webapp/src/app/@shared/datastore/helpers.ts